import { useCallback, useState } from "react";
import { Box, DialogActions, DialogContent, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import ActionButton from "../../Button/ActionButton";
import DialogComponent from "../../Dialog/Dialog";
import DocumentUploadField from "../DocumentUploadField/DocumentUploadField";
import ConfigurationSection from "../ConfigurationSection/ConfigurationSection";
import {
  DocumentGroupEntry,
  GROUP_DOCUMENT_TYPES,
  buildIdGroup,
  isGroupComplete
} from "../../../pages/PaymentConfigurations/documents/documentMatrix";
import { DocumentGroupKey } from "../../../types/payments";
import { semanticColors } from "../../../utils/colors";
import { typography } from "../../../utils/typography";

/**
 * A repeatable group of documents — beneficial owners or legal representatives.
 *
 * At least one entry is required and every entry needs all three documents.
 */
export default function DocumentGroupSection({
  configId,
  groupKey,
  title,
  addLabel,
  removeLabel,
  entryLabel,
  entries,
  onAddEntry,
  onRemoveEntry,
  onChange,
  readOnly = false
}: {
  configId: string;
  groupKey: DocumentGroupKey;
  title: string;
  addLabel: string;
  removeLabel: string;
  entryLabel: string;
  entries: DocumentGroupEntry[];
  onAddEntry: () => void;
  onRemoveEntry: (entry: DocumentGroupEntry) => Promise<void>;
  onChange: () => void;
  /** Lists the entries without offering to add or remove any. */
  readOnly?: boolean;
}) {
  const { t } = useTranslation();
  const [
    pendingRemoval,
    setPendingRemoval
  ] = useState<DocumentGroupEntry | null>(null);

  const confirmRemoval = useCallback(async () => {
    if (!pendingRemoval) return;
    const entry = pendingRemoval;
    setPendingRemoval(null);
    await onRemoveEntry(entry);
  }, [pendingRemoval, onRemoveEntry]);

  return (
    <ConfigurationSection title={title} valid={isGroupComplete(entries)}>
      {!readOnly && (
        <Box sx={{ display: "flex", justifyContent: "flex-start" }}>
          {/* Secondary: the primary action of this screen is confirming the
              documents, not adding another entry. */}
          <ActionButton
            label={addLabel}
            onClick={onAddEntry}
            variant="secondary"
          />
        </Box>
      )}

      {entries.map((entry, position) => (
        <Box
          key={entry.index}
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1,
            mt: 2,
            p: 2,
            borderRadius: "8px",
            backgroundColor: semanticColors.surface.containerLow
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2
            }}
          >
            <Typography
              sx={{
                ...typography.body.lBold,
                color: semanticColors.text.primary
              }}
            >
              {t("paymentDocuments.entry", {
                label: entryLabel,
                number: position + 1
              })}
            </Typography>
            {!readOnly && (
              <ActionButton
                label={removeLabel}
                onClick={() => setPendingRemoval(entry)}
                variant="danger"
              />
            )}
          </Box>

          {GROUP_DOCUMENT_TYPES.map(documentType => (
            <DocumentUploadField
              key={documentType}
              configId={configId}
              documentType={documentType}
              idGroup={buildIdGroup(groupKey, entry.index)}
              document={entry.documents[documentType]}
              onChange={onChange}
              readOnly={readOnly}
            />
          ))}
        </Box>
      ))}

      <DialogComponent
        open={Boolean(pendingRemoval)}
        handleModal={() => setPendingRemoval(null)}
        headerTxt={t("paymentDocuments.removeEntryConfirmTitle")}
      >
        <DialogContent>
          <Typography
            sx={{ ...typography.body.l, color: semanticColors.text.primary }}
          >
            {t("paymentDocuments.removeEntryConfirmMessage")}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Box display="flex" gap={1} p={1}>
            <ActionButton
              label={t("common.no")}
              onClick={() => setPendingRemoval(null)}
              variant="secondary"
            />
            <ActionButton
              label={t("common.yes")}
              onClick={confirmRemoval}
              variant="danger"
            />
          </Box>
        </DialogActions>
      </DialogComponent>
    </ConfigurationSection>
  );
}
